/**
 * FlashBook themes — deliberately separate from the capsule theme registry.
 * Capsule themes live in `#/themes/registry`; FlashBook themes live here so the
 * two features never share theme ids. HTML themes for FlashBook are listed in
 * `#/lib/htmlThemesFlashbook` and rendered through the same postMessage bridge
 * as capsule HTML themes (protocol v4: page_show / block_answer).
 */
import {type FlashBookTheme, type FlashBookThemeItem} from '#/types/flashbook'

export const FLASHBOOK_THEME_REGISTRY: Record<string, FlashBookTheme> = {
  storybook: {
    id: 'storybook',
    name: 'Storybook',
    version: '1.0.0',
    description: 'Warm paper pages, cozy reading feel',
    previewColor: '#c2410c',
    category: 'stories',
    musicTrack: 'storybook',
    tokens: {
      bg: '#1a0f08',
      bgSecondary: '#2a1a0c',
      pageBg: '#fbf3e0',
      pageBorder: '#e6d5ad',
      cardBg: '#fffdf7',
      cardBorder: '#e0cda0',
      textPrimary: '#3b2a12',
      textSecondary: '#5f4a2c',
      textMuted: '#8a6b40',
      accent: '#c2410c',
      isLight: true,
      // The progress track sits on the dark shell, never on the paper page.
      progressTrack: 'rgba(255,255,255,0.14)',
      progressFill: '#c2410c',
      pageTurn: 'slide',
    },
    meta: {
      recommendedFor: "Children's stories, narrative reading",
      ageRange: '4–10',
      contentTypes: ['content', 'single_choice', 'true_false', 'fill_blank'],
      layoutMode: 'open page with a flourished rule between sections',
      illustrationStyle: 'warm paper, chapter ornament, no hard boxes',
      motionStyle: 'page-like turns',
      readingMode: '"Read to me" set into the page as a quiet text action',
    },
  },
  notebook: {
    id: 'notebook',
    name: 'Notebook',
    version: '1.0.0',
    description: 'Ruled-line pages, classroom feel',
    previewColor: '#2563eb',
    category: 'education',
    musicTrack: 'notebook',
    tokens: {
      bg: '#0b1220',
      bgSecondary: '#0f1a2e',
      pageBg: '#f7f9fc',
      pageBorder: '#d9e2ee',
      cardBg: '#ffffff',
      cardBorder: '#cdd9e8',
      textPrimary: '#0f172a',
      textSecondary: '#475569',
      textMuted: '#64748b',
      accent: '#2563eb',
      isLight: true,
      progressTrack: 'rgba(255,255,255,0.14)',
      progressFill: '#2563eb',
      pageTurn: 'slide',
    },
    meta: {
      recommendedFor: 'Classroom exercises, worksheets, revision',
      ageRange: '8–16',
      contentTypes: [
        'single_choice',
        'multiple_choice',
        'fill_blanks',
        'complex_fill_blank',
      ],
      layoutMode: 'ruled sheet, every block an exercise under a rule',
      illustrationStyle: 'blue ink, margin rule, lettered answers',
      motionStyle: 'soft ink transitions',
      readingMode: 'compact listen pill in the page margin',
    },
  },
  academy: {
    id: 'academy',
    name: 'Academy',
    version: '1.0.0',
    description: 'Clean dark indigo, focused study mode',
    // One shade deeper than indigo-500 so white labels on accent-filled
    // buttons (Check / Continue) clear 4.5:1 instead of landing at 4.47:1.
    previewColor: '#5b5ce8',
    category: 'education',
    musicTrack: 'academy',
    tokens: {
      bg: '#070b18',
      bgSecondary: '#0c1226',
      pageBg: '#0f1730',
      pageBorder: '#26325a',
      cardBg: '#141e3c',
      cardBorder: '#2c3a68',
      textPrimary: '#eef2ff',
      textSecondary: '#a5b4fc',
      textMuted: '#8b9ac4',
      accent: '#5b5ce8',
      isLight: false,
      progressTrack: 'rgba(255,255,255,0.14)',
      progressFill: '#5b5ce8',
      pageTurn: 'fade',
    },
    meta: {
      recommendedFor: 'Serious study, exam preparation, university material',
      ageRange: '15+',
      contentTypes: [
        'single_choice',
        'multiple_choice',
        'matching',
        'ordering',
      ],
      layoutMode: 'centred structured panels, strong hierarchy',
      illustrationStyle: 'restrained, minimal decoration',
      motionStyle: 'minimal',
      readingMode: 'professional inline audio control',
    },
  },
  magazine: {
    id: 'magazine',
    name: 'Magazine',
    version: '1.0.0',
    description: 'Light editorial layouts',
    previewColor: '#0891b2',
    category: 'editorial',
    musicTrack: 'magazine',
    tokens: {
      bg: '#081418',
      bgSecondary: '#0b1c22',
      pageBg: '#f2f6f9',
      pageBorder: '#d3e0e8',
      cardBg: '#ffffff',
      cardBorder: '#ccdde5',
      textPrimary: '#0f172a',
      textSecondary: '#475569',
      textMuted: '#64748b',
      accent: '#0891b2',
      isLight: true,
      progressTrack: 'rgba(255,255,255,0.14)',
      progressFill: '#0891b2',
      pageTurn: 'none',
    },
    meta: {
      recommendedFor: 'Editorial articles, modern educational content',
      ageRange: '13+',
      contentTypes: ['content', 'single_choice', 'image_choice', 'accordion'],
      layoutMode: 'editorial columns with pull-rule section breaks',
      illustrationStyle: 'masthead ornament, squared answer slabs',
      motionStyle: 'almost none',
      readingMode: 'editorial audio badge',
    },
  },
  'space-adventure': {
    id: 'space-adventure',
    name: 'Space Adventure',
    version: '1.0.0',
    description: 'Cosmic mission theme for young explorers',
    // Violet-500 left white button labels at 4.23:1; this shade keeps the
    // cosmic feel while clearing AA.
    previewColor: '#7c4ddb',
    category: 'kids',
    musicTrack: 'space-adventure',
    tokens: {
      bg: '#0a0618',
      bgSecondary: '#120a26',
      pageBg: '#150f2e',
      pageBorder: '#322660',
      cardBg: '#201749',
      cardBorder: '#3d2d7e',
      textPrimary: '#f3eefe',
      textSecondary: '#c4b5fd',
      textMuted: '#9d8fd0',
      accent: '#7c4ddb',
      isLight: false,
      progressTrack: 'rgba(255,255,255,0.14)',
      progressFill: '#7c4ddb',
      pageTurn: 'fade',
    },
    meta: {
      recommendedFor: 'Gamified learning, young explorers, quests',
      ageRange: '7–13',
      contentTypes: [
        'single_choice',
        'multiple_choice',
        'drag_words',
        'find_words',
      ],
      layoutMode: 'mission panels over a starfield page',
      illustrationStyle: 'starfield, dashed mission markers',
      motionStyle: 'orbital drift',
      readingMode: 'mission audio badge',
    },
  },
  laboratory: {
    id: 'laboratory',
    name: 'Laboratory',
    version: '1.0.0',
    description: 'Lab notebook, measurement panels and data tables',
    previewColor: '#0f766e',
    category: 'science',
    musicTrack: 'academy',
    tokens: {
      bg: '#04140f',
      bgSecondary: '#061c15',
      pageBg: '#f1f7f5',
      pageBorder: '#c9ded8',
      cardBg: '#ffffff',
      cardBorder: '#bcd6d0',
      textPrimary: '#08221c',
      textSecondary: '#33544c',
      textMuted: '#5b756f',
      accent: '#0f766e',
      isLight: true,
      progressTrack: 'rgba(255,255,255,0.14)',
      progressFill: '#0f766e',
      pageTurn: 'fade',
    },
    meta: {
      recommendedFor:
        'Science lessons, experiments, method sheets, data reading',
      ageRange: '11–18',
      contentTypes: [
        'content',
        'table_slide',
        'formula_slide',
        'single_choice',
        'fill_blanks',
      ],
      layoutMode: 'measurement paper with bounded observation panels',
      illustrationStyle: 'grid paper, boxed markers, squared data tables',
      motionStyle: 'minimal',
      readingMode: 'instrument badge beside the method text',
    },
  },
  mathematics: {
    id: 'mathematics',
    name: 'Mathematics',
    version: '1.0.0',
    description: 'Squared paper, worked examples, chalk formulas',
    previewColor: '#4338ca',
    category: 'maths',
    musicTrack: 'academy',
    tokens: {
      bg: '#0b0c1f',
      bgSecondary: '#111233',
      pageBg: '#f6f7fc',
      pageBorder: '#d6d9ec',
      cardBg: '#ffffff',
      cardBorder: '#c9cde4',
      textPrimary: '#14163a',
      textSecondary: '#3f4470',
      textMuted: '#686d99',
      accent: '#4338ca',
      isLight: true,
      progressTrack: 'rgba(255,255,255,0.14)',
      progressFill: '#4338ca',
      pageTurn: 'none',
    },
    meta: {
      recommendedFor: 'Maths exercises, geometry, algebra and problem solving',
      ageRange: '10–18',
      contentTypes: [
        'formula_slide',
        'table_slide',
        'single_choice',
        'multiple_choice',
        'ordering',
      ],
      layoutMode: 'squared paper with numbered exercise rows',
      illustrationStyle: 'graph grid, chalk notation, ruled solutions',
      motionStyle: 'soft ink',
      readingMode: 'plain read-to-me line under the statement',
    },
  },
  comic: {
    id: 'comic',
    name: 'Comic',
    version: '1.0.0',
    description: 'Bold inked panels, speech and big answer bubbles',
    previewColor: '#b91c1c',
    category: 'comics',
    musicTrack: 'space-adventure',
    tokens: {
      bg: '#170a0a',
      bgSecondary: '#241010',
      pageBg: '#fffdf6',
      pageBorder: '#f0d9b5',
      cardBg: '#ffffff',
      cardBorder: '#1f2937',
      textPrimary: '#111827',
      textSecondary: '#374151',
      textMuted: '#6b7280',
      accent: '#b91c1c',
      isLight: true,
      progressTrack: 'rgba(255,255,255,0.14)',
      progressFill: '#b91c1c',
      pageTurn: 'slide',
    },
    meta: {
      recommendedFor: 'Stories, dialogues, visual learning, reluctant readers',
      ageRange: '7–14',
      contentTypes: ['content', 'image_choice', 'single_choice', 'true_false'],
      layoutMode: 'heavy inked panels with speech-style callouts',
      illustrationStyle: 'thick borders, round bubbles, lettered markers',
      motionStyle: 'panel-page turns',
      readingMode: 'read-to-me pill under the panel text',
    },
  },
  heritage: {
    id: 'heritage',
    name: 'Heritage',
    version: '1.0.0',
    description: 'Parchment, archival plates and small-caps captions',
    previewColor: '#9a6a1f',
    category: 'culture',
    musicTrack: 'storybook',
    tokens: {
      bg: '#140f07',
      bgSecondary: '#221a0e',
      pageBg: '#f7efdd',
      pageBorder: '#e0cfa6',
      cardBg: '#fdf9ee',
      cardBorder: '#ddc9a0',
      textPrimary: '#3a2c12',
      textSecondary: '#5d4a28',
      textMuted: '#836c45',
      accent: '#9a6a1f',
      isLight: true,
      progressTrack: 'rgba(255,255,255,0.14)',
      progressFill: '#9a6a1f',
      pageTurn: 'slide',
    },
    meta: {
      recommendedFor: 'History, culture, manuscripts, museums and heritage',
      ageRange: '11+',
      contentTypes: [
        'content',
        'table_slide',
        'formula_slide',
        'single_choice',
        'matching',
      ],
      layoutMode: 'open parchment page with ornamented plates',
      illustrationStyle: 'aged paper, flourished rules, thin archival frames',
      motionStyle: 'page-like turns',
      readingMode: 'quiet listen line set into the page',
    },
  },
}

export function getFlashbookTheme(themeId: string): FlashBookTheme {
  return FLASHBOOK_THEME_REGISTRY[themeId] ?? FLASHBOOK_THEME_REGISTRY.storybook
}

export function listFlashbookThemes(): FlashBookTheme[] {
  return Object.values(FLASHBOOK_THEME_REGISTRY)
}

/**
 * Unified item list for the FlashBook theme selector: native defaults + HTML
 * bridge themes. HTML themes are never capsule themes — separate list file.
 */
export function listFlashbookThemeItems(): FlashBookThemeItem[] {
  const natives: FlashBookThemeItem[] = listFlashbookThemes().map(th => ({
    kind: 'native',
    id: th.id,
    name: th.name,
    emoji: undefined,
    description: th.description,
    category: th.category,
    previewColor: th.previewColor,
    meta: th.meta,
  }))

  // Lazy-import to avoid a hard dependency cycle between registry and lib.

  const {BUILTIN_FLASHBOOK_HTML_THEMES} =
    require('#/lib/htmlThemesFlashbook') as {
      BUILTIN_FLASHBOOK_HTML_THEMES: {
        id: string
        name: string
        emoji: string
        path: string
        category: string
      }[]
    }

  const htmls: FlashBookThemeItem[] = BUILTIN_FLASHBOOK_HTML_THEMES.map(th => ({
    kind: 'html',
    id: th.id,
    name: th.name,
    emoji: th.emoji,
    description: `HTML theme — ${th.name}`,
    category: th.category,
    htmlPath: th.path,
    previewColor: '#14b8a6',
  }))

  return [...natives, ...htmls]
}
